import { useEffect, useMemo, useState, MouseEvent as ReactMouseEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonStyles,
  Card,
  DropDown,
  FAIcon,
  HeaderStyle,
  IconStyle,
  MLPVersion,
  ProfileImage,
  Tag,
  TagStyle,
  TeamProgressBar,
  Theme,
  useBreakpoint,
} from '../../../ui/components';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import ParticipantService from '../../../sdk/services/participantService';
import AttachmentService from '../../../sdk/services/attachmentService';
import { useAPICall } from '../../../hooks';
import { computeMemberProgress, isMemberLocked, LoggerService } from '../../../ui/utils';
import { DynamicObjectInterface } from '../../../sdk/type';
import { MenuDataType } from '../../home/Home';
import EmptyStateImage from '../../../assets/image/operationCard/Check_Eligibility.png';
import styles from './MyTeamMulti.module.scss';

interface MyTeamMultiPropTypes {
  dataCampaign: MenuDataType;
  decodedData: DynamicObjectInterface;
}

const ALL = '__ALL__';

const MyTeamMulti = ({ dataCampaign, decodedData }: MyTeamMultiPropTypes) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const campaignId = dataCampaign?.campaignId;
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;

  const [teams, setTeams] = useState<DynamicObjectInterface[]>([]);
  const [profileImageUrls, setProfileImageUrls] = useState<Record<string, string | undefined>>({});

  const [teamFilter, setTeamFilter] = useState<string[]>([]);
  const [challengeFilter, setChallengeFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);

  const configJson = dataCampaign?.configJson;
  const subChallenges: DynamicObjectInterface[] = configJson?.activities?.subChallenges ?? [];

  const subChallengeName = (id?: string): string =>
    subChallenges.find(sc => sc.subChallengeId === id)?.name ?? t('dashboard.my-team.label.dateNotAvailable');

  // Configured check-in days for the member's sub-challenge ([] when none).
  const checkInDaysFor = (subChallengeId?: string): (number | string)[] =>
    subChallenges.find(sc => sc.subChallengeId === subChallengeId)?.checkIn?.days ?? [];

  // The challenge is over once the CAMPAIGN end date has passed; after that incomplete
  // steps can no longer be worked on (shown red on the progress bar + "Incomplete" tag).
  // Gated on campaignEnd (not teamEndDate) to match the My Progress tracker — a step
  // stays actionable until the campaign ends even if the team's own window has passed.
  const isChallengeEnded = (team: DynamicObjectInterface): boolean => {
    if (!team?.campaignEnd) return false;
    const endDate = new Date(team.campaignEnd);
    if (isNaN(endDate.getTime())) return false;
    endDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() > endDate.getTime();
  };

  // The team leader is the member whose ABO id (loginId) matches the team's
  // coachAboId. Compared as strings to avoid number/string type mismatches.
  const isTeamLeaderMember = (team: DynamicObjectInterface, member: DynamicObjectInterface): boolean =>
    !!team?.coachAboId && `${team.coachAboId}` === `${member?.loginId}`;

  // The Member Status filter is only relevant to the team leader (COACH), who
  // oversees the whole team. Regular members don't see it.
  const isCurrentUserTeamLeader = !!dataCampaign?.enrollment?.some(
    (e: DynamicObjectInterface) => e.enrollmentType === 'COACH',
  );

  // A member's status: COMPLETED (post-assessment done) > INCOMPLETE (campaign ended
  // with post-assessment still not done — can no longer be worked on, mirrors the
  // My Progress tracker's red-cross "Incomplete") > LOCKED (team not started) >
  // IN_PROGRESS. `locked` is the team-level lock from isMemberLocked(team).
  const getMemberStatus = (member: DynamicObjectInterface, locked: boolean, campaignEnded: boolean): string => {
    const progress = computeMemberProgress(member, checkInDaysFor(member?.subChallengeId));
    if (progress.isAllWeightOut) return 'COMPLETED';
    if (campaignEnded) return 'INCOMPLETE';
    return locked ? 'LOCKED' : 'IN_PROGRESS';
  };

  useEffect(() => {
    (async () => {
      const coachId = dataCampaign?.enrollment?.find(
        (i: DynamicObjectInterface) => i.enrollmentType === 'COACH',
      )?.coachId;
      const result = await apiCall(ParticipantService.getTeamListByUser, {
        campaignId: dataCampaign?.campaignId,
        loginId: decodedData?.loginId,
        userId: decodedData?.userId,
        coachId,
      });
      setTeams(result?.data?.data ?? []);
    })();
  }, [dataCampaign?.campaignId]);

  const fetchProfileImage = async (userId: number) => {
    try {
      const response = await AttachmentService.downloadProfileImage(userId ?? '');
      if (response?.data?.data?.image) {
        setProfileImageUrls(prev => ({ ...prev, [userId]: response.data.data.image }));
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    teams.forEach(team =>
      (team?.participants ?? []).forEach((p: DynamicObjectInterface) => fetchProfileImage(p.userId)),
    );
  }, [teams]);

  const teamOptions: DropDownData[] = useMemo(
    () => teams.map(team => ({ id: team.teamId, name: team.teamName })),
    [teams],
  );

  const challengeOptions: DropDownData[] = useMemo(
    () => [
      { id: ALL, name: t('dashboard.my-team.label.filterAll') },
      ...subChallenges.map(sc => ({ id: sc.subChallengeId, name: sc.name })),
    ],
    [subChallenges, t],
  );


  const statusOptions: DropDownData[] = useMemo(
    () => [
      { id: ALL, name: t('dashboard.my-team.label.filterAll') },
      { id: 'IN_PROGRESS', name: t('dashboard.my-team.label.statusInProgress') },
      { id: 'COMPLETED', name: t('dashboard.my-team.label.statusCompleted') },
      { id: 'INCOMPLETE', name: t('dashboard.my-progress.incompleteLabel') },
      { id: 'LOCKED', name: t('dashboard.my-team.label.statusLocked') },
    ],
    [t],
  );

  const visibleTeams = useMemo(() => {
    return teams
      .filter(team => teamFilter.length === 0 || teamFilter.includes(`${team.teamId}`))
      .map(team => {
        const locked = isMemberLocked(team);
        const campaignEnded = isChallengeEnded(team);
        // A team is incomplete when it hasn't reached its configured size (solo `ind`
        // teams are never "incomplete"). Count only ACTIVE registered members.
        const teamSize = Number(team?.teamSize) || 0;
        const activeCount = (team?.participants ?? []).filter(
          (p: DynamicObjectInterface) => p?.status === 'ACTIVE',
        ).length;
        const isIncomplete =
          team?.participationType !== 'ind' && teamSize > 0 && activeCount < teamSize;
        const members = (team?.participants ?? [])
          .filter((member: DynamicObjectInterface) => {
            if (challengeFilter !== ALL && member?.subChallengeId !== challengeFilter) return false;
            if (statusFilter !== ALL) {
              if (getMemberStatus(member, locked, campaignEnded) !== statusFilter) return false;
            }
            return true;
          })
          // Team leader always first, then remaining members sorted alphabetically by name.
          .sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => {
            const aLeader = isTeamLeaderMember(team, a);
            const bLeader = isTeamLeaderMember(team, b);
            if (aLeader !== bLeader) return aLeader ? -1 : 1;
            return `${a?.name ?? ''}`.localeCompare(`${b?.name ?? ''}`, undefined, { sensitivity: 'base' });
          });
        return { ...team, locked, isIncomplete, teamSize, activeCount, filteredMembers: members };
      })
      .filter(team => team.filteredMembers.length > 0);
  }, [teams, teamFilter, challengeFilter, statusFilter]);

  const renderMemberRow = (team: DynamicObjectInterface, member: DynamicObjectInterface) => {
    const progress = computeMemberProgress(member, checkInDaysFor(member?.subChallengeId));
    const isYou = member.userId === decodedData?.userId;
    const isLeader = isTeamLeaderMember(team, member);
    const memberStatus = getMemberStatus(member, team.locked, isChallengeEnded(team));
    const statusLabel =
      memberStatus === 'COMPLETED'
        ? t('dashboard.my-team.label.statusCompleted')
        : memberStatus === 'INCOMPLETE'
          ? t('dashboard.my-progress.incompleteLabel')
          : memberStatus === 'LOCKED'
            ? t('dashboard.my-team.label.statusLocked')
            : t('dashboard.my-team.label.statusInProgress');
    const statusTheme =
      memberStatus === 'COMPLETED'
        ? Theme.SUCCESS
        : memberStatus === 'INCOMPLETE'
          ? Theme.ERROR
          : memberStatus === 'LOCKED'
            ? Theme.GRAY
            : Theme.WARNING;

    // Clicking your own row opens My Progress (for the clicked team); another member
    // opens their detail. Pass tId so My Progress preselects the right team.
    const handleRedirect = () =>
      isYou
        ? navigate(`/campaigns/${campaignId}?tId=${team.teamId}`)
        : navigate(`/campaigns/${campaignId}/myTeam/user?id=${member.userId}&tId=${team.teamId}`);

    return (
      <div
        key={member.recId ?? member.userId}
        className={styles['member-row']}
        role="button"
        tabIndex={0}
        onClick={handleRedirect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRedirect();
          }
        }}
      >
        <div className={styles['member-identity']}>
          <ProfileImage
            placeholder={member.name ?? ''}
            src={profileImageUrls[member.userId]}
            classes={styles['member-avatar']}
          />
          <div className={styles['member-name-block']}>
            <div className={styles['member-name']}>
              {member.loginId} - {member.name}
              {isLeader && (
                <span className={styles['leader-badge']}>
                  {t('dashboard.my-team.progress-tracker.label.teamLeader')}
                </span>
              )}
              {isYou && <span className={styles['you-badge']}>{t('dashboard.my-team.label.you')}</span>}
            </div>
            <div className={styles['member-category']}>
              {t('dashboard.my-team.label.challenge')}: {subChallengeName(member.subChallengeId)}
            </div>
          </div>
        </div>
        <div className={styles['member-progress']}>
          <TeamProgressBar progress={progress} locked={team.locked} campaignEnded={isChallengeEnded(team)} />
        </div>
        <div className={styles['member-status']}>
          <Tag text={statusLabel} theme={statusTheme} style={TagStyle.SOLID} />
        </div>
      </div>
    );
  };

  // Reset all filters to their default (All).
  const resetFilters = () => {
    setTeamFilter([]);
    setChallengeFilter(ALL);
    setStatusFilter(ALL);
  };

  return (
    <div className={styles['my-team-multi']}>
      <Card
        title={t('dashboard.sidebar.label.myTeam')}
        headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      >
        <div className={styles['my-team-desc']}>{t('dashboard.my-team.label.myTeamDesc')}</div>
        <div className={styles['filters']}>
          <DropDown
            id="filter-team"
            name="filter-team"
            mlpVersion={MLPVersion.ONE}
            dropdownLabel={t('dashboard.my-team.label.filterTeam')}
            placeholder={t('dashboard.my-team.label.filterAll')}
            data={teamOptions}
            multiSelection
            allSelectionOption
            value={teams.filter(team => teamFilter.includes(`${team.teamId}`)).map(team => team.teamName)}
            configData={{
              label: 'name',
              onSelect: (selected: DropDownData[]) => setTeamFilter(selected.map(o => `${o.id}`)),
            }}
          />
          <DropDown
            id="filter-challenge"
            name="filter-challenge"
            mlpVersion={MLPVersion.ONE}
            dropdownLabel={t('dashboard.my-team.label.filterChallenge')}
            data={challengeOptions}
            value={challengeOptions.find(o => o.id === challengeFilter)?.name as string}
            configData={{
              label: 'name',
              onSelect: (selected: DropDownData) => setChallengeFilter(`${selected.id}`),
            }}
          />
          {/* Member Status filter is for the team leader only. */}
          {isCurrentUserTeamLeader && (
            <DropDown
              id="filter-status"
              name="filter-status"
              mlpVersion={MLPVersion.ONE}
              dropdownLabel={t('dashboard.my-team.label.memberStatus')}
              data={statusOptions}
              value={statusOptions.find(o => o.id === statusFilter)?.name as string}
              configData={{
                label: 'name',
                onSelect: (selected: DropDownData) => setStatusFilter(`${selected.id}`),
              }}
            />
          )}
          <div className={styles['filter-reset']}>
            <Button
              title={t('dashboard.my-team.button.reset')}
              theme={Theme.GREEN}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              onClick={resetFilters}
            />
          </div>
        </div>

        {/* No team/member matches the active filters — show the shared empty state
            (reuses the "check eligibility" illustration + standard no-results copy). */}
        {visibleTeams.length === 0 && (
          <div className={styles['empty-state']}>
            <img
              src={EmptyStateImage}
              alt={t('dashboard.my-team.label.noResultTitle')}
              className={styles['empty-state__image']}
            />
            <div className={styles['empty-state__title']}>{t('dashboard.my-team.label.noResultTitle')}</div>
            <div className={styles['empty-state__subtitle']}>
              {t('dashboard.my-team.label.noResultDescription')}
            </div>
          </div>
        )}

        {visibleTeams.map(team => (
          <Accordion
            key={team.teamId}
            id={`team-${team.teamId}`}
            title={`${team.teamName} (${team.filteredMembers.length})`}
            isOpen
            headerClassName={team.isIncomplete ? styles['incomplete-header'] : ''}
            childrenTitle={
              team.isIncomplete ? (
                <span className={styles['team-incomplete']}>
                  <FAIcon
                    iconName="circle-exclamation"
                    iconStyle={IconStyle.SOLID}
                    theme={Theme.ERROR}
                    className={styles['team-incomplete-icon']}
                  />
                  <span className={styles['team-incomplete-count']}>
                    {`${team.activeCount}/${team.teamSize} ${t('dashboard.my-team.label.members')}`}
                  </span>
                  {/* Only the team leader (the team's COACH, whose ABO id === the
                      logged-in user's loginId) can add members. */}
                  {!!team.coachAboId && `${team.coachAboId}` === `${decodedData?.loginId}` && (
                    <span
                      role="button"
                      tabIndex={0}
                      className={styles['team-add-member']}
                      onClick={(e: ReactMouseEvent) => {
                        // Inside the Accordion header button — don't toggle the accordion.
                        e.stopPropagation();
                        navigate(`/campaigns/teams/add-participant?=${team.teamId}&cmp=${campaignId}`);
                      }}
                      onKeyDown={(e: ReactKeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/campaigns/teams/add-participant?=${team.teamId}&cmp=${campaignId}`);
                        }
                      }}
                    >
                      <FAIcon iconName="plus" iconStyle={IconStyle.SOLID} theme={Theme.RED} />
                      <span className={styles['team-add-member-text']}>
                        {t('dashboard.my-team.button.addMember')}
                      </span>
                    </span>
                  )}
                </span>
              ) : undefined
            }
          >
            <div className={styles['member-list']}>
              {team.filteredMembers.map((member: DynamicObjectInterface) => renderMemberRow(team, member))}
            </div>
          </Accordion>
        ))}
      </Card>
    </div>
  );
};

export default MyTeamMulti;
